import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Check, ArrowRight, RefreshCw } from 'lucide-react';
import { useCurrency } from '../utils/currency';
export function BonusThreeSection() {
  const {
    formatPrice
  } = useCurrency();
  const includes = ['7 mensajes de follow-up listos (copiar/pegar)', 'Timing exacto (24h / 48h / 72h / día 5 / día 7 / día 10 / día 14+)', 'Cómo reactivar 20 chats fríos en 10 minutos', '6 reactivadores extra por escenario (visto post-precio, "lo pienso", descuento, etc.)', 'Cierre elegante final (para cerrar sin quemar el contacto)'];
  const bullets = ['El mensaje exacto para mandar 24 horas después del silencio', 'Cómo hacer que el "lo pienso" se convierta en decisión', 'La frase de cierre elegante que deja la puerta abierta sin perseguir', 'Qué decir cuando ya pasaron 2 semanas de silencio', 'Cómo reactivar 20 chats en 10 minutos (proceso paso a paso)'];
  return <section className="w-full bg-white py-20 px-4 sm:px-6 lg:px-8" data-id="element-138">
      <div className="max-w-6xl mx-auto" data-id="element-139">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start" data-id="element-140">
          {/* Image — LEFT */}
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex justify-center lg:justify-start" data-id="element-141">
            <div className="relative max-w-xs w-full" data-id="element-142">
              <img src="https://cdn.magicpatterns.com/uploads/oggrsKb4TS9TZn3DszXJs3/bono3_follow_up_inteligente.png" alt="Bono 3: Follow-up Inteligente" className="w-full h-auto object-contain drop-shadow-xl rounded-lg" data-id="element-143" />
              <div className="absolute -top-3 -right-3 bg-[#0B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg" data-id="element-144">
                Valor: {formatPrice(13)}
              </div>
            </div>
          </motion.div>

          {/* Text — RIGHT */}
          <motion.div initial={{
          opacity: 0,
          x: 24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="flex flex-col gap-5" data-id="element-145">
            {/* Badge */}
            <div className="flex items-center gap-2" data-id="element-146">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0B6B3A] text-white text-sm font-bold rounded-full" data-id="element-147">
                <Gift className="w-4 h-4" data-id="element-148" />
                BONO #3 — GRATIS
              </span>
              <span className="text-sm text-[#6B7280] line-through" data-id="element-149">
                {formatPrice(13)}
              </span>
            </div>

            {/* Title */}
            <div data-id="element-150">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] leading-tight mb-2" data-id="element-151">
                Follow-up Inteligente: 7 Mensajes para Reactivar Conversaciones
              </h2>
              <p className="text-lg text-[#6B7280] font-medium flex items-center gap-2" data-id="element-152">
                <RefreshCw className="w-4 h-4 text-[#0B6B3A]" data-id="element-153" />
                Sin perseguir. Sin rogar. Sin quemar la relación.
              </p>
            </div>

            {/* Hook */}
            <p className="text-base text-[#1A1A1A] leading-relaxed" data-id="element-154">
              Tenés ventas durmiendo en tus DMs ahora mismo. Gente que te
              preguntó, le interesó, y desapareció. No porque decidió que no.
              Sino porque <strong data-id="element-155">nadie la volvió a guiar</strong>.
            </p>

            {/* Problem scenario */}
            <div className="bg-[#F9FAFB] border-l-4 border-[#0B6B3A] rounded-r-xl p-5" data-id="element-156">
              <p className="text-sm font-bold text-[#1A1A1A] mb-2 uppercase tracking-wide" data-id="element-157">
                El error más común:
              </p>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-158">
                La mayoría hace 1 de 2 cosas: no hace follow-up (por miedo a
                "molestar") o hace demasiado (y espanta). Ninguna funciona. Lo
                que funciona es una{' '}
                <strong className="text-[#1A1A1A]" data-id="element-159">
                  secuencia con timing + intención + cierre elegante
                </strong>
                .
              </p>
            </div>

            {/* Includes */}
            <div data-id="element-160">
              <p className="text-sm font-bold text-[#1A1A1A] uppercase tracking-wide mb-3" data-id="element-161">
                Lo que incluye:
              </p>
              <ul className="space-y-2" data-id="element-162">
                {includes.map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: 10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.07
              }} className="flex items-start gap-2.5 text-sm text-[#1A1A1A]" data-id="element-163">
                    <Check className="w-4 h-4 text-[#0B6B3A] mt-0.5 flex-shrink-0" data-id="element-164" />
                    {item}
                  </motion.li>)}
              </ul>
            </div>

            {/* Value justification */}
            <p className="text-sm text-[#6B7280] italic border-t border-gray-100 pt-4" data-id="element-165">
              Recuperar 1 sola venta que ya estaba "perdida" paga este bono
              decenas de veces. Y si hacés el proceso de los 20 chats fríos, lo
              más probable es que recuperes más de una.
            </p>

            {/* Bullets */}
            <ul className="space-y-1.5" data-id="element-166">
              {bullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#6B7280]" data-id="element-167">
                  <ArrowRight className="w-3.5 h-3.5 text-[#0B6B3A] mt-0.5 flex-shrink-0" data-id="element-168" />
                  {b}
                </li>)}
            </ul>

            <p className="text-xs text-[#6B7280] font-medium" data-id="element-169">
              Formato: mini-guía PDF + secuencia copiable + calendario de
              timing. Acceso inmediato.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}